import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, Check, Loader2, RotateCcw, Search, ShieldCheck, Sliders, X,
} from 'lucide-react';
import {
  UI_FEATURES, UI_FEATURE_GROUPS, capabilitiesOf, type UiFeatureFlagKey,
} from '@repo/shared-types';
import { AdminSidebar } from '../../components/layout/AdminSidebar';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Toggle } from '../../components/ui/toggle';
import { api, type AdminUser } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { cn } from '../../lib/utils';

function StatusBadge({ status }: { status: AdminUser['accountStatus'] }) {
  if (status === 'APPROVED') return <Badge variant="success">Approved</Badge>;
  if (status === 'WAITING_APPROVAL') return <Badge variant="warning">Pending</Badge>;
  return <Badge variant="danger">Suspended</Badge>;
}

function UserRow({
  user, active, onClick,
}: {
  user: AdminUser;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left px-3 py-2.5 rounded-md transition-colors',
        active ? 'bg-surface-muted' : 'hover:bg-surface-subtle',
      )}
    >
      <div className="flex items-center gap-1.5">
        <span className={cn('text-xs font-medium truncate flex-1', active ? 'text-ink' : 'text-ink-secondary')}>
          {user.name ?? user.email}
        </span>
        {user.isSystemAdmin && (
          <span title="System admin" className="flex-shrink-0 flex items-center">
            <ShieldCheck size={11} className="text-ink-faint" />
          </span>
        )}
      </div>
      <div className="flex items-center gap-1.5 mt-0.5">
        {user.name && <span className="text-[10px] text-ink-faint truncate flex-1">{user.email}</span>}
        {user.hasOverrides ? (
          <span className="text-[9px] font-medium text-orange-600 dark:text-orange-400 flex-shrink-0">
            custom
          </span>
        ) : (
          <span className="text-[9px] text-ink-faint flex-shrink-0">full access</span>
        )}
      </div>
    </button>
  );
}

/**
 * Feature-permission editor for one user.
 *
 * Edits are held locally as a set of switched-off keys and saved in one PUT, rather than firing a
 * request per toggle: a permission set is one coherent decision, and a half-applied config from a
 * dropped request is worse than an unsaved one. `disabled` from the server is the baseline, so the
 * dirty check is a straight set comparison.
 */
function FeatureEditor({
  user, onSaved,
}: {
  user: AdminUser;
  onSaved: (updated: AdminUser) => void;
}) {
  const { user: currentUser } = useAuth();
  const [disabled, setDisabled] = useState<Set<UiFeatureFlagKey>>(new Set(user.disabled));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setDisabled(new Set(user.disabled));
    setError(null);
    setSaved(false);
  }, [user.id, user.disabled]);

  const baseline = useMemo(() => new Set(user.disabled), [user.disabled]);
  const dirty =
    disabled.size !== baseline.size || [...disabled].some((key) => !baseline.has(key));

  const toggle = (key: UiFeatureFlagKey, enabled: boolean) => {
    setSaved(false);
    setDisabled((prev) => {
      const next = new Set(prev);
      // Enabling removes the override entirely rather than storing an explicit true — default is
      // on, so the two are equivalent and the table stays free of no-op rows.
      if (enabled) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await api.admin.setFeatures(user.id, { disabled: [...disabled] });
      onSaved(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const isSelf = currentUser?.id === user.id;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-7 pt-6 pb-4 border-b border-border-subtle flex-shrink-0">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-base font-bold text-ink leading-tight">{user.name ?? user.email}</h1>
            {user.name && <p className="text-xs text-ink-faint mt-0.5">{user.email}</p>}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <StatusBadge status={user.accountStatus} />
            {user.isSystemAdmin && <Badge variant="info">System admin</Badge>}
          </div>
        </div>

        {isSelf && (
          <p className="mt-3 text-[11px] text-ink-muted bg-surface-subtle border border-border rounded px-3 py-1.5">
            This is your own account. Sections you switch off here disappear from your sidebar on your
            next page load — admin access is separate, so you won't lose this console.
          </p>
        )}

        {user.accountStatus !== 'APPROVED' && (
          <p className="mt-3 text-[11px] text-warning-text bg-warning-bg border border-warning-ring rounded px-3 py-1.5 flex items-start gap-1.5">
            <AlertTriangle size={12} className="mt-px flex-shrink-0" />
            This account is {user.accountStatus === 'WAITING_APPROVAL' ? 'awaiting approval' : 'suspended'} and
            can't sign in, so these settings won't take effect until it's approved under Accounts.
          </p>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-7 py-5">
        <p className="text-xs text-ink-muted mb-5 max-w-xl leading-relaxed">
          Everything is available by default. Switch something off to take it away from this person.
          A group switch overrides everything inside it.
        </p>

        <div className="space-y-5">
          {UI_FEATURE_GROUPS.map((group) => {
            const groupOff = disabled.has(group.key);
            const features = UI_FEATURES.filter((f) => f.group === group.group);

            return (
              <div key={group.key} className="border border-border rounded-lg overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-2.5 bg-surface-subtle border-b border-border-subtle">
                  <Toggle
                    checked={!groupOff}
                    onChange={(next) => toggle(group.key, next)}
                    label={`${group.label} group`}
                  />
                  <span className="text-xs font-semibold text-ink flex-1">{group.label}</span>
                  {groupOff && (
                    <span className="text-[10px] font-medium text-red-600 dark:text-red-400">
                      whole group hidden
                    </span>
                  )}
                </div>

                <div className={cn('divide-y divide-border-subtle', groupOff && 'opacity-45')}>
                  {features.map((definition) => {
                    const featureOff = disabled.has(definition.feature);
                    const capabilities = capabilitiesOf(definition.feature);
                    return (
                      <div key={definition.feature}>
                        <div className="flex items-center gap-3 px-4 py-2">
                          <Toggle
                            checked={!featureOff}
                            onChange={(next) => toggle(definition.feature, next)}
                            // Pointless to fiddle with an individual switch while the group above
                            // it is off — the group wins, so the control would lie.
                            disabled={groupOff}
                            label={definition.label}
                          />
                          <span className="text-xs text-ink-secondary flex-1">{definition.label}</span>
                          <span className="text-[10px] font-mono text-ink-faint">{definition.path}</span>
                          {featureOff && !groupOff && (
                            <span className="text-[10px] font-medium text-red-600 dark:text-red-400 w-12 text-right">
                              hidden
                            </span>
                          )}
                          {(!featureOff || groupOff) && <span className="w-12" />}
                        </div>

                        {/* Switches *inside* this section — indented under it because that is the
                            relationship, and switching the section off takes them with it whatever
                            they say individually (the same rule the group above applies to it). */}
                        {capabilities.length > 0 && (
                          <div className="pl-11 pr-4 pb-2 space-y-1.5">
                            {capabilities.map((capability) => {
                              const capabilityOff = disabled.has(capability.capability);
                              const inert = groupOff || featureOff;
                              return (
                                <div key={capability.capability} className="flex items-start gap-3">
                                  <Toggle
                                    checked={!capabilityOff}
                                    onChange={(next) => toggle(capability.capability, next)}
                                    disabled={inert}
                                    label={capability.label}
                                  />
                                  <span className={cn('min-w-0 flex-1', inert && 'opacity-45')}>
                                    <span className="block text-[11px] text-ink-secondary">
                                      {capability.label}
                                    </span>
                                    <span className="block text-[10px] text-ink-faint leading-snug">
                                      {capability.description}
                                    </span>
                                  </span>
                                  {capabilityOff && !inert && (
                                    <span className="text-[10px] font-medium text-red-600 dark:text-red-400 w-12 text-right flex-shrink-0">
                                      hidden
                                    </span>
                                  )}
                                  {(!capabilityOff || inert) && <span className="w-12 flex-shrink-0" />}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="px-7 py-3 border-t border-border-subtle flex-shrink-0 flex items-center justify-between gap-3 bg-surface-subtle">
        <div className="min-w-0">
          {error && (
            <p className="text-[11px] text-danger-text flex items-center gap-1.5">
              <X size={11} />{error}
            </p>
          )}
          {!error && saved && (
            <p className="text-[11px] text-green-600 dark:text-green-400 flex items-center gap-1.5">
              <Check size={11} />Saved — takes effect on their next page load
            </p>
          )}
          {!error && !saved && dirty && (
            <p className="text-[11px] text-ink-muted">
              {disabled.size} override{disabled.size === 1 ? '' : 's'} pending
            </p>
          )}
          {!error && !saved && !dirty && (
            <p className="text-[11px] text-ink-faint">
              {baseline.size === 0 ? 'Full access — no overrides' : `${baseline.size} override${baseline.size === 1 ? '' : 's'} saved`}
            </p>
          )}
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <Button
            size="sm"
            onClick={() => setDisabled(new Set())}
            disabled={saving || disabled.size === 0}
            title="Clear every override, restoring full access"
          >
            <RotateCcw size={12} className="mr-1.5" />Full access
          </Button>
          <Button size="sm" onClick={() => setDisabled(new Set(user.disabled))} disabled={saving || !dirty}>
            Revert
          </Button>
          <Button variant="primary" size="sm" onClick={save} disabled={saving || !dirty}>
            {saving ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <Check size={12} className="mr-1.5" />}
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminFeatures() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api.admin
      .listUsers()
      .then((list) => {
        setUsers(list);
        setSelectedId((current) => current ?? list[0]?.id ?? null);
        setError(null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return users;
    return users.filter(
      (u) => u.email.toLowerCase().includes(needle) || (u.name ?? '').toLowerCase().includes(needle),
    );
  }, [users, filter]);

  const selected = users.find((u) => u.id === selectedId) ?? null;

  // Patch in place rather than refetching the list: a full reload would re-sort and could move
  // the row you just edited out from under the cursor.
  const applyUpdate = (updated: AdminUser) =>
    setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));

  const withOverrides = users.filter((u) => u.hasOverrides).length;

  return (
    <div className="flex h-screen overflow-hidden bg-surface">
      <AdminSidebar />

      <div className="pl-56 flex h-full w-full overflow-hidden">
        <div className="w-72 flex-shrink-0 h-full bg-card border-r border-border flex flex-col overflow-hidden">
          <div className="px-3 py-3 border-b border-border space-y-2.5">
            <div className="flex items-center gap-2">
              <Sliders size={13} className="text-ink-faint" />
              <span className="text-sm font-semibold text-ink">Feature Permissions</span>
            </div>
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint" />
              <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter users…"
                className="w-full pl-7 pr-3 py-1.5 text-xs bg-surface rounded-md border-none outline-none placeholder:text-ink-faint text-ink"
              />
            </div>
            <p className="text-[10px] text-ink-faint">
              {users.length} user{users.length === 1 ? '' : 's'} · {withOverrides} with custom access
            </p>
          </div>

          <div className="flex-1 overflow-y-auto px-2 py-2">
            {loading ? (
              <p className="px-2 py-4 text-xs text-ink-faint">Loading…</p>
            ) : filtered.length === 0 ? (
              <p className="px-2 py-4 text-xs text-ink-faint">No matching users</p>
            ) : (
              <div className="space-y-0.5">
                {filtered.map((user) => (
                  <UserRow
                    key={user.id}
                    user={user}
                    active={user.id === selectedId}
                    onClick={() => setSelectedId(user.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 h-full overflow-hidden bg-card min-w-0">
          {error ? (
            <div className="p-7">
              <p className="text-xs text-danger-text bg-danger-bg border border-danger-ring rounded px-3 py-2">{error}</p>
            </div>
          ) : selected ? (
            <FeatureEditor user={selected} onSaved={applyUpdate} />
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-xs text-ink-faint">Select a user to edit their access.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
